import { create } from 'zustand';
import { exchangeToken, getMe, logout as apiLogout } from '../lib/api';

/**
 * @typedef {Object} UserProfile
 * @property {string} user_id
 * @property {string} name
 * @property {string} role
 * @property {string} college_id
 * @property {string} college_name
 * @property {'taskverse'|'placevalue'} [auth_source]
 */

const TASKVERSE_URL = (process.env.REACT_APP_TASKVERSE_URL || '').replace(/\/+$/, '');

/** Only the student flow is actually built on the Taskverse side today —
 * other roles fall back to the Taskverse root rather than a 404. */
const TASKVERSE_DASHBOARD_BY_ROLE = {
  student: 'student/dashboard',
  trainer: 'trainer/dashboard',
  collegeadmin: 'college-admin/dashboard',
  superadmin: 'super-admin/dashboard',
};

function taskverseReturnUrl(role) {
  const path = TASKVERSE_DASHBOARD_BY_ROLE[(role || '').toLowerCase()] || '';
  return TASKVERSE_URL ? `${TASKVERSE_URL}/${path}` : '';
}

/**
 * Auth store using zustand.
 * States: loading=true (checking), user=object (authed), user=null (no session).
 */
const useAuthStore = create((set, get) => ({
  /** @type {UserProfile|null} */
  user: null,
  loading: true,
  error: null,

  /**
   * Try to restore session from stored token via /auth/me.
   */
  checkSession: async () => {
    const storedToken = sessionStorage.getItem('botview_session_token');
    if (!storedToken) {
      set({ user: null, loading: false });
      return;
    }
    set({ loading: true, error: null });
    try {
      const user = await getMe();
      set({ user, loading: false });
    } catch {
      sessionStorage.removeItem('botview_session_token');
      set({ user: null, loading: false });
    }
  },

  /**
   * Exchange a JWT from the URL for a session token.
   * @param {string} token
   */
  exchangeToken: async (token) => {
    set({ loading: true, error: null });
    try {
      const data = await exchangeToken(token);
      const { session_token, ...user } = data;
      set({ user, loading: false });
      return user;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Token exchange failed';
      set({ user: null, loading: false, error: detail });
      return null;
    }
  },

  /**
   * Logout — clear token and state. If the session came from Taskverse,
   * send the student back to their Taskverse dashboard instead of falling
   * through to the PlaceValue auth gate.
   */
  logout: async () => {
    const { user } = get();
    const returnUrl = user?.auth_source === 'taskverse' ? taskverseReturnUrl(user.role) : '';

    await apiLogout();
    set({ user: null, loading: false, error: null });

    if (returnUrl) {
      window.location.href = returnUrl;
    }
  },
}));

export default useAuthStore;
